import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import CompanyDiagnosisService from "../../../../../service/CompanyDiagnosisService";

const STEP_SECTIONS = [
  "company_registry",
  "company_structure",
  "company_size",
  "human_capital_gender",
  "human_capital_roles",
  "human_capital",
  "representations",
  "other_info",
];

const getEmptyFormData = () => ({
  meta: {
    status: "DRAFT",
    consent: false,
    date: null,
    created_at: null,
    consultant: null,
  },
  company_registry: {},
  company_structure: {},
  company_size: {},
  human_capital_gender: {},
  human_capital_roles: {},
  human_capital: {},
  representations: {},
  other_info: {},
});

const diagnosisService = new CompanyDiagnosisService();

export default function useBusinessDiagnosisForm() {
  const [formData, setFormData] = useState(getEmptyFormData);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diagnosisId, setDiagnosisId] = useState(null);

  const { search } = useLocation();
  const queryId = new URLSearchParams(search).get("id");
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("__extraconomy_token") : null;

  const dispatchProgressUpdate = useCallback((status) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("businessDiagnosisProgressUpdated", {
        detail: { status },
      }),
    );
  }, []);

  const loadDiagnosis = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    const empty = getEmptyFormData();
    const normalizePayload = (p) => p?.company_diagnosis ?? p;
    const getIdFromPayload = (p) => p?.meta?.id ?? p?.id ?? p?.company_diagnosis?.id;

    const buildFallbackProgressFromContent = (payload) => {
      const completed = new Set();
      STEP_SECTIONS.forEach((section, i) => {
        const sectionData = payload?.[section];
        if (
          sectionData &&
          typeof sectionData === "object" &&
          Object.keys(sectionData).length > 0
        ) {
          completed.add(i + 1);
        }
      });

      const firstIncompleteIdx = STEP_SECTIONS.findIndex((_, i) => !completed.has(i + 1));
      const nextStep = firstIncompleteIdx === -1 ? 8 : firstIncompleteIdx + 1;
      return { completed, nextStep };
    };

    const computeProgressFromMetaStatus = (payload) => {
      const statusRaw = payload?.meta?.status;
      const status =
        typeof statusRaw === "string" ? statusRaw.trim().toUpperCase() : statusRaw;

      if (status === "COMPLETED") {
        const completed = new Set([1, 2, 3, 4, 5, 6, 7, 8]);
        return { completed, nextStep: 8 };
      }

      if (typeof status === "string" && /^STEP\d+$/.test(status)) {
        const stepNum = Number(status.replace("STEP", ""));
        const safeStep = Math.min(Math.max(stepNum, 1), 7);
        const completed = new Set([1, 2, 3, 4, 5, 6, 7, 8].filter((n) => n <= safeStep));
        return { completed, nextStep: Math.min(safeStep + 1, 8) };
      }

      if (!status || status === "DRAFT") {
        return { completed: new Set(), nextStep: 1 };
      }

      return buildFallbackProgressFromContent(payload);
    };

    try {
      setLoading(true);

      let raw = null;
      if (queryId) {
        const res = await diagnosisService.getDiagnosisById(token, queryId);
        raw = res;
      } else {
        const res = await diagnosisService.getAllDiagnoses(token);
        const list = res?.result ?? res?.diagnoses ?? res;
        raw = Array.isArray(list) ? list[0] : list;
      }

      const payload = normalizePayload(raw);

      if (payload && typeof payload === "object") {
        const { completed, nextStep } = computeProgressFromMetaStatus(payload);
        const diagnosisIdFromPayload = getIdFromPayload(payload);

        setFormData({
          meta: { ...empty.meta, ...(payload.meta || {}) },
          company_registry: payload.company_registry ?? {},
          company_structure: payload.company_structure ?? {},
          company_size: payload.company_size ?? {},
          human_capital_gender: payload.human_capital_gender ?? {},
          human_capital_roles: payload.human_capital_roles ?? {},
          human_capital: payload.human_capital ?? {},
          representations: payload.representations ?? {},
          other_info: payload.other_info ?? {},
        });
        setDiagnosisId(diagnosisIdFromPayload ? String(diagnosisIdFromPayload) : queryId || null);
        setCompletedSteps(completed);
        setCurrentStep(nextStep);
      } else {
        setFormData(empty);
        setDiagnosisId(null);
        setCompletedSteps(new Set());
        setCurrentStep(1);
      }
    } catch (err) {
      setFormData(getEmptyFormData());
      setDiagnosisId(null);
      setCompletedSteps(new Set());
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  }, [token, queryId]);

  useEffect(() => {
    loadDiagnosis();
  }, [loadDiagnosis]);

  const updateSection = useCallback((sectionKey, values) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), ...values },
    }));
  }, []);

  const handleStepSubmit = useCallback(
    async (stepIndex, stepValues) => {
      if (!token) return;
      const sectionKey = STEP_SECTIONS[stepIndex - 1];
      if (!sectionKey) return;

      setSaving(true);
      try {
        const sectionPayload = { ...(formData[sectionKey] || {}), ...stepValues };
        if (stepIndex === 8 && stepValues.consent !== undefined) {
          sectionPayload.consent = undefined;
          delete sectionPayload.consent;
        }
        const nextData = {
          ...formData,
          [sectionKey]: sectionPayload,
        };

        const nextStatus = stepIndex === 8 ? "COMPLETED" : `STEP${stepIndex}`;
        nextData.meta = { ...(nextData.meta || {}), status: nextStatus };

        if (stepIndex === 8 && stepValues.consent !== undefined) {
          nextData.meta = { ...nextData.meta, consent: !!stepValues.consent };
        }

        if (diagnosisId) {
          await diagnosisService.updateDiagnosis(token, diagnosisId, nextData);
        } else {
          const created = await diagnosisService.createDiagnosis(token, nextData);
          const createdId =
            created?.company_diagnosis?.id ??
            created?.meta?.id ??
            created?.id ??
            created?.company_diagnosis?.meta?.id;

          if (createdId) {
            setDiagnosisId(String(createdId));
          } else {
            const res = await diagnosisService.getAllDiagnoses(token);
            const list = res?.result ?? res?.diagnoses ?? res;
            const raw = Array.isArray(list) ? list[0] : list;
            const payload = raw?.company_diagnosis ?? raw;
            const existingId = payload?.meta?.id ?? payload?.id;
            if (existingId) {
              setDiagnosisId(String(existingId));
              await diagnosisService.updateDiagnosis(token, String(existingId), nextData);
            }
          }
        }
        setFormData(nextData);
        setCompletedSteps((prev) => new Set([...prev, stepIndex]));
        if (stepIndex < 8) setCurrentStep(stepIndex + 1);
        dispatchProgressUpdate(nextData?.meta?.status);
        return nextData;
      } catch (err) {
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [token, diagnosisId, formData, dispatchProgressUpdate]
  );

  const goToStep = useCallback((stepNumber) => {
    const n = Number(stepNumber);
    if (n >= 1 && n <= 8) setCurrentStep(n);
  }, []);

  const markAsCompleted = useCallback(async (overrideData) => {
    if (!token || !diagnosisId) return;
    setSaving(true);
    try {
      const baseData = overrideData ?? formData;
      const nextData = {
        ...baseData,
        meta: { ...(baseData.meta || {}), status: "COMPLETED" },
      };
      await diagnosisService.updateDiagnosis(token, diagnosisId, nextData);
      setFormData(nextData);
      dispatchProgressUpdate("COMPLETED");
    } finally {
      setSaving(false);
    }
  }, [token, diagnosisId, formData, dispatchProgressUpdate]);

  const setFormDataSection = useCallback((sectionKey, values) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: typeof values === "function" ? values(prev[sectionKey] || {}) : { ...(prev[sectionKey] || {}), ...values },
    }));
  }, []);

  return {
    formData,
    setFormData,
    setFormDataSection,
    currentStep,
    setCurrentStep,
    completedSteps,
    loading,
    saving,
    diagnosisId,
    handleStepSubmit,
    goToStep,
    markAsCompleted,
    updateSection,
    queryId,
  };
}
